/**
 * SDK-layer ("rich") views over the canonical `EventItem`, which lives in
 * `@tento/lona/sheets` (the lower data-model layer).
 *
 * These helpers depend on SDK-only concerns — `Network.*` wire types,
 * `EventsPlatform`, `EventCategory` colors, and `CalendarLookup` calendar
 * metadata — so they cannot live on the core class in the `sheets` layer
 * (which must stay DOM/Network-free and is depended on by `src`). Callers in
 * `@tento/lona`, `@tento/lona/sheets-ui`, and browser hosts use these in place of
 * the rich methods the duplicate `EventItem` used to carry.
 */

import type { EventId } from "@tento-lona/sheets";
import { type Option, Patch, type Unsubscribe } from "@tento-lona/sheets";
import { DateTime, TimezoneRegion, Utc } from "@tento-chrono";

import { EventItem, EventItemTimed } from "./event-item.ts";
import { type EventCategories, EventCategory, type EventCategoryItem } from "./event-category-item.ts";
import type { Network } from "../network.ts";
import type {
  AuthScopedCalendarDisplay,
  CalendarLookup,
} from "./calendar-types.ts";
import { AuthScopedCalendarId, calendarWritable } from "./calendar-types.ts";
import type { EventsPlatform } from "../services/events/events-platform.ts";
import type { CalendarEvent, EventOrigin } from "@tento-lona/sheets";
import type { RecurrenceInfo } from "@tento-lona/sheets/platform";
import { CalendarId, eventDbToEvent } from "@tento-lona/sheets";
import type { DateFormat, TimeFormat } from "../preferences/calendar-preferences.ts";


/**
 * The open event drafts' live previews of one client
 * (`client.events.drafts`): what an open event editor shows before it saves
 * — a title typed, a category picked — so every surface that paints the
 * event (`EventItemPresentation.title` / `categoryName` / `backgroundColor`)
 * shows the draft, and repaints when it changes (`subscribe`). Per client:
 * one client's editor never repaints another client's events.
 */
export class EventDrafts {
  readonly #previews = new Map<string, EventItemPresentation.DraftPreview>();
  readonly #listeners = new Set<() => void>();

  /**
   * Show `preview` for `eventId` (replacing its previous one) and notify
   * every subscriber. A preview that sets no field clears the draft.
   */
  set(eventId: EventId, preview: EventItemPresentation.DraftPreview): void {
    if (
      preview.title === undefined &&
      preview.category === undefined &&
      preview.suggestedCategory === undefined &&
      preview.categoryChoice === undefined
    ) {
      this.clear(eventId);
      return;
    }
    this.#previews.set(eventId, preview);
    this.#notify();
  }

  /** Drop `eventId`'s draft preview; notifies only when there was one. */
  clear(eventId: EventId): void {
    if (!this.#previews.delete(eventId)) return;
    this.#notify();
  }

  /** The draft preview shown for `eventId`, or `null`. */
  peek(eventId: EventId): Option<EventItemPresentation.DraftPreview> {
    return this.#previews.get(eventId) ?? null;
  }

  /** Call `listener` on every draft change; returns its unsubscribe. */
  onChange(listener: () => void): Unsubscribe {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  #notify(): void {
    for (const listener of this.#listeners) listener();
  }
}

/** A draft's category choice as the event's `useSuggestedCategory` spells it: `null` for `Patch.clear` (the automatic choice). */
function useSuggestedOf(choice: Patch.Clearable<EventItemPresentation.CategoryPick>): Option<boolean> {
  const pick = Patch.wire(choice);
  return pick == null ? null : pick === "suggested";
}

/**
 * How an event item is presented: its draft preview, category and colours,
 * and the network updates a presentation applies. The reads that depend on
 * the client — its category table and its open drafts — take them as a
 * `EventItemPresentation.Scope` (`client.events` is one).
 */
export namespace EventItemPresentation {
  /**
   * An open editor's draft, as an overlay on the event it edits, in the
   * `Patch` vocabulary: a field left out shows the event's own value,
   * `Patch.clear` clears it (no title, no category, the automatic category
   * choice) and a value is the draft's. `categoryChoice` is which category
   * the draft shows: `"suggested"`, `"own"`, or `Patch.clear` for the
   * automatic choice (the suggestion when there is no own category).
   * `EventDrafts.set` with no field clears the draft.
   *
   * ```ts
   * client.events.drafts.set(event.id, { title: "Standup", categoryChoice: "own" });
   * ```
   */
  export type DraftPreview = {
    title?: Patch.Clearable<string>;
    category?: Patch.Clearable<EventCategory>;
    suggestedCategory?: Patch.Clearable<EventCategory>;
    categoryChoice?: Patch.Clearable<CategoryPick>;
  };

  /** Which category a draft shows (`DraftPreview.categoryChoice`): the suggested one or its own. */
  export type CategoryPick = "suggested" | "own";

  /**
   * What a presentation read resolves against: the client's category table
   * (`client.events.categories`) and its open drafts
   * (`client.events.drafts`). `client.events` is a `Scope`; a host with no
   * client builds `{ categories: new EventCategories(appearance), drafts:
   * new EventDrafts() }`.
   */
  export type Scope = {
    readonly categories: EventCategories;
    readonly drafts: EventDrafts;
  };

  /**
   * Projection of an event onto the SDK-side list-row props bag
   * (`rowProps`): a read, every field stated.
   */
  export type RowProps = {
    readonly id: EventId;
    readonly title: string;
    readonly isEditable: boolean;
    readonly options: {
      readonly calendarId: CalendarId;
      readonly calendarColor: string;
      readonly time: DateTime<Utc>;
      readonly tz: TimezoneRegion;
      readonly onClick: () => void;
    };
  };

  /** The category `e` is tagged with, as `categories` holds it; `null` when untagged. */
  export function explicitCategory(e: EventItem, categories: EventCategories): Option<EventCategoryItem> {
    return categories.itemOpt(EventCategory.parse(e.inner.category ?? null));
  }

  /** The category suggested for `e`, as `categories` holds it; `null` when none. */
  export function suggestedCategory(e: EventItem, categories: EventCategories): Option<EventCategoryItem> {
    return categories.itemOpt(EventCategory.parse(e.inner.suggestedCategory ?? null));
  }

  /**
   * An event's category fields, as the event (or its open draft) states
   * them: its own category, the suggested one, and its choice between them —
   * `true` shows the suggestion, `false` its own, `null` leaves it to
   * `resolvedUseSuggestedCategory` (the suggestion when it has none of its own).
   */
  export type CategoryChoice = {
    readonly category: Option<EventCategory>;
    readonly suggestedCategory: Option<EventCategory>;
    readonly useSuggestedCategory: Option<boolean>;
  };

  /** Whether `choice` shows the suggested category: its explicit choice, else the suggestion when it has no own category. */
  export function resolvedUseSuggestedCategory(choice: CategoryChoice): boolean {
    if (choice.useSuggestedCategory != null) return choice.useSuggestedCategory;
    return choice.category == null && choice.suggestedCategory != null;
  }

  /** The category `choice` shows (see `resolvedUseSuggestedCategory`); `null` when none. */
  export function resolvedCategory(choice: CategoryChoice): Option<EventCategory> {
    return resolvedUseSuggestedCategory(choice) ? choice.suggestedCategory : choice.category;
  }

  /** The category `e` shows — its open draft's, else its own (see `resolvedCategory`). */
  export function categoryName(e: EventItem, scope: Scope): Option<EventCategory> {
    const preview = scope.drafts.peek(e.id);
    const category = preview?.category !== undefined
      ? Patch.wire(preview.category)
      : EventCategory.parse(e.inner.category ?? null);
    const suggestedCategory = preview?.suggestedCategory !== undefined
      ? Patch.wire(preview.suggestedCategory)
      : EventCategory.parse(e.inner.suggestedCategory ?? null);
    const useSuggestedCategory = preview?.categoryChoice !== undefined
      ? useSuggestedOf(preview.categoryChoice)
      : e.inner.useSuggestedCategory ?? null;
    return resolvedCategory({ category, suggestedCategory, useSuggestedCategory });
  }

  /** The category item `e` shows (`categoryName`), as `scope`'s table holds it. */
  export function categoryItem(e: EventItem, scope: Scope): Option<EventCategoryItem> {
    return scope.categories.itemOpt(categoryName(e, scope));
  }

  /** The title `e` shows — its open draft's, else its own. */
  export function title(e: EventItem, scope: Scope): string {
    const preview = scope.drafts.peek(e.id);
    if (preview?.title !== undefined) {
      return Patch.wire(preview.title) ?? "";
    }
    return e.title();
  }

  /** `e`'s category colour in `scope`'s appearance, else `var(--gray)`. */
  export function backgroundColor(e: EventItem, scope: Scope): string {
    return categoryItem(e, scope)?.colorOpt() ?? "var(--gray)";
  }

  /** `e`'s dimmed category colour in `scope`'s appearance, else `var(--gray-dimmed)`. */
  export function dimmedBackgroundColor(e: EventItem, scope: Scope): string {
    return categoryItem(e, scope)?.colorDimmedOpt() ?? "var(--gray-dimmed)";
  }

  export function acal(
    e: EventItem,
    calendars: CalendarLookup,
  ): Option<AuthScopedCalendarDisplay> {
    const acid = AuthScopedCalendarId.parse(e.acid[0], e.acid[1]);
    return acid == null ? null : calendars.peek(acid);
  }

  export function isEditable(e: EventItem, calendars: CalendarLookup): boolean {
    const cal = acal(e, calendars);
    if (!cal) return false;
    const cald = cal[1];
    if (!calendarWritable(cald)) {
      return false;
    }
    if ((e.inner.eventType ?? "default") !== "default") return false;
    // Owner/writer access to the calendar already grants edit rights. `creatorIsSelf`
    // is an unreliable editability signal on secondary/group calendars: Google
    // attributes an event's creator to the calendar's own identity
    // (…@group.calendar.google.com), so it reports creator.self=false even for events
    // you created on a calendar you OWN — which wrongly opened them read-only (copy- or
    // create-to-secondary-calendar). Don't let that flag block editing when you own the
    // calendar; keep it as a guard only for non-owned (writer-shared) calendars.
    if (cald.accessRole !== "owner" && !e.inner.creatorIsSelf) return false;
    return true;
  }

  export function rowProps(
    e: EventItemTimed,
    scope: Scope,
    platform: EventsPlatform,
    tz: TimezoneRegion,
    calendars: CalendarLookup,
    timeFormat: TimeFormat,
    dateFormat: DateFormat,
    /** The access of the list showing the row (`CalendarListConfig.access`), forwarded to `showEventDetails` (required: see there). */
    list: "editable" | "read-only",
  ): RowProps {
    const rawColor = backgroundColor(e, scope);
    const calendarColor = rawColor !== "var(--gray)" ? rawColor : "var(--bg)";
    return {
      id: e.id,
      title: title(e, scope),
      isEditable: isEditable(e, calendars),
      options: {
        calendarId: e.acid[1],
        calendarColor,
        time: e.time.time.start,
        tz,
        onClick: () => {
          platform.showEventDetails(e, tz, calendars, timeFormat, dateFormat, list);
        },
      },
    };
  }

  /** Construct an `EventItem` from a network-wire `EventCell` via the decoder. */
  export function fromEventDb(
    db: Network.EventCell,
    recurrenceInfo: Option<RecurrenceInfo>,
    origin: EventOrigin,
  ): Option<EventItem> {
    const ev: Option<CalendarEvent> = eventDbToEvent(db);
    if (!ev) return null;
    return EventItem.make(ev, recurrenceInfo, origin);
  }
}
